import { Link, useNavigate } from 'react-router-dom';

const LOGO_URL = 'https://public.readdy.ai/ai/img_res/2f4f4ce3-8121-45e6-bf04-63921ac249c0.png';

const quickLinks = [
  { label: 'Inicio', to: '/' },
  { label: 'Nosotros', to: '/nosotros' },
  { label: 'Servicios', to: '/servicios' },
  { label: 'Opiniones', to: '/opiniones' },
  { label: 'Sucursales', to: '/sucursales' },
  { label: 'Galería', to: '/galeria' },
  { label: 'Contacto', to: '/contacto' },
];

const services = [
  'Rehabilitación Dental',
  'Limpieza y Prevención',
  'Estética Dental',
  'Prótesis y Coronas',
  'Ortodoncia',
];

const legalLinks = [
  { label: 'Aviso de Privacidad', path: '/legal/aviso-de-privacidad' },
  { label: 'Términos y Condiciones', path: '/legal/terminos-y-condiciones' },
  { label: 'Compromiso General', path: '/legal/condiciones-generales' },
  { label: 'Descarga de Responsabilidades', path: '/legal/descarga-de-responsabilidades' },
];

export default function Footer() {
  const navigate = useNavigate();
  const scrollTo = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer id="footer" className="bg-[#061827] relative overflow-hidden">
      {/* Top accent line */}
      <div className="h-1 bg-gradient-to-r from-transparent via-[#3A86FF] to-transparent" />

      {/* Decorative bg */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#3A86FF]/5 rounded-full blur-[120px]" />

      {/* Map strip */}
      <div className="relative w-full h-56 overflow-hidden">
        <iframe
          title="Ubicación MF Dental"
          src="https://maps.google.com/maps?q=Av.+504+51,+Pueblo+San+Juan+de+Aragón,+Gustavo+A.+Madero,+CDMX&output=embed"
          className="w-full h-full border-0 grayscale contrast-125 brightness-75"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#061827]/40 to-[#061827]/20 pointer-events-none" />
      </div>

      {/* Main footer content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-10">
          {/* Brand column */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center">
                <img src={LOGO_URL} alt="MF Dental" className="h-10 w-auto object-contain" />
              </div>
              <div>
                <span className="block text-white font-bold text-lg">MF Dental</span>
                <span className="block text-[#3A86FF] text-xs">Rehabilitación Dental</span>
              </div>
            </div>
            <p className="text-white/45 text-sm leading-relaxed mb-6">
              Consultorio odontológico privado especializado en rehabilitación dental en Ciudad de México. Tu sonrisa es nuestra misión.
            </p>
            {/* Vision */}
            <div className="border-l-2 border-[#3A86FF] pl-4">
              <p className="text-white/50 text-xs italic leading-relaxed">
                "Ser una clínica dental referente en Ciudad de México por nuestra calidad, innovación y trato humano."
              </p>
            </div>

            {/* Social links */}
            <div className="flex gap-3 mt-6">
              <a
                href="https://www.facebook.com/MF-Dental"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center bg-white/8 hover:bg-[#3A86FF]/30 rounded-xl text-white/60 hover:text-[#3A86FF] transition-all duration-200 cursor-pointer"
              >
                <i className="ri-facebook-fill text-lg" />
              </a>
              <a
                href="https://www.instagram.com/mfdental02"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center bg-white/8 hover:bg-[#3A86FF]/30 rounded-xl text-white/60 hover:text-[#3A86FF] transition-all duration-200 cursor-pointer"
              >
                <i className="ri-instagram-line text-lg" />
              </a>
              <a
                href="https://wa.me/525534748982"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center bg-white/8 hover:bg-[#25D366]/30 rounded-xl text-white/60 hover:text-[#25D366] transition-all duration-200 cursor-pointer"
              >
                <i className="ri-whatsapp-line text-lg" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-6 text-sm tracking-wide uppercase">Navegación</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="flex items-center gap-2 text-white/45 hover:text-[#3A86FF] text-sm transition-colors duration-200 cursor-pointer whitespace-nowrap group"
                  >
                    <div className="w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <i className="ri-arrow-right-s-line text-xs" />
                    </div>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold mb-6 text-sm tracking-wide uppercase">Servicios</h4>
            <ul className="space-y-3">
              {services.map((svc) => (
                <li key={svc}>
                  <button
                    onClick={() => scrollTo('#services')}
                    className="flex items-center gap-2 text-white/45 hover:text-[#3A86FF] text-sm transition-colors duration-200 cursor-pointer whitespace-nowrap group"
                  >
                    <div className="w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <i className="ri-arrow-right-s-line text-xs" />
                    </div>
                    {svc}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold mb-6 text-sm tracking-wide uppercase">Contacto</h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-[#3A86FF]/15 rounded-lg flex-shrink-0 mt-0.5">
                  <i className="ri-map-pin-2-line text-[#3A86FF] text-sm" />
                </div>
                <div>
                  <p className="text-white/75 text-sm font-medium">Dirección</p>
                  <p className="text-white/45 text-xs leading-relaxed mt-0.5">
                    Av. 504 #51, Pueblo San Juan de Aragón,<br />Gustavo A. Madero, CDMX
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-[#3A86FF]/15 rounded-lg flex-shrink-0">
                  <i className="ri-phone-line text-[#3A86FF] text-sm" />
                </div>
                <div>
                  <p className="text-white/75 text-sm font-medium">Teléfono</p>
                  <a href="tel:5534748982" className="text-white/45 text-xs hover:text-[#3A86FF] transition-colors cursor-pointer whitespace-nowrap">
                    55 3474 8982
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-[#3A86FF]/15 rounded-lg flex-shrink-0">
                  <i className="ri-time-line text-[#3A86FF] text-sm" />
                </div>
                <div>
                  <p className="text-white/75 text-sm font-medium">Horario</p>
                  <p className="text-white/45 text-xs mt-0.5">Lunes – Sábado</p>
                  <p className="text-white/45 text-xs">Abierto hasta las 8:00 p.m.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-[#3A86FF]/15 rounded-lg flex-shrink-0">
                  <i className="ri-star-line text-[#3A86FF] text-sm" />
                </div>
                <div>
                  <p className="text-white/75 text-sm font-medium">Calificación</p>
                  <div className="flex items-center gap-1 mt-0.5">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-400 text-xs" />
                    ))}
                    <span className="text-white/45 text-xs ml-1">5.0 (15)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/8">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4">
          {/* Legal links row */}
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            {legalLinks.map((link) => (
              <button
                key={link.path}
                onClick={() => navigate(link.path)}
                className="text-white/30 hover:text-[#3A86FF] text-xs transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                {link.label}
              </button>
            ))}
          </div>
          {/* Bottom row */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 pt-3 border-t border-white/5">
            <p className="text-white/30 text-sm text-center">
              © {new Date().getFullYear()} MF Dental · Todos los derechos reservados
            </p>
            <div className="flex items-center gap-2 text-white/25 text-xs">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-shield-check-line text-[#3A86FF]" />
              </div>
              Clínica dental certificada · Ciudad de México
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
